import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "./api";
import { useAuth } from "./AuthContext";
import { serviceList } from "../data/mock";
import { HERO_VIDEO_URL } from "../data/mock";
import { translations } from "../i18n/translations";
import { DEFAULT_STATS } from "../components/HeroDashboard";
import { Switch } from "../components/ui/switch";
import { privacyPolicyDefault } from "../data/privacyPolicy";
import { termsConditionsDefault } from "../data/termsConditions";
import { indiaMapDefault, INDIA_STATES } from "../data/indiaMap";
import {
  LogOut, Users, MessageSquareQuote, HelpCircle, Phone, LayoutGrid, Bot,
  Trash2, Plus, Save, Loader2, UploadCloud, Image as ImageIcon, Home, FileText, Shield, ScrollText,
  Plug, RefreshCw,
} from "lucide-react";
import { CratioSection } from "./CratioSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

const TABS = [
  { id: "leads", label: "Leads", icon: Users },
  { id: "homepage", label: "Homepage", icon: Home },
  { id: "about", label: "About Page", icon: FileText },
  { id: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { id: "faqs", label: "FAQs", icon: HelpCircle },
  { id: "contact", label: "Contact Info", icon: Phone },
  { id: "services", label: "Services", icon: LayoutGrid },
  { id: "ai", label: "AI Concierge", icon: Bot },
  { id: "privacy", label: "Privacy Policy", icon: Shield },
  { id: "terms", label: "Terms & Conditions", icon: ScrollText },
  { id: "integrations", label: "Integrations", icon: Plug },
];

const inputCls = "w-full rounded-lg border border-[#12284B]/20 px-3 py-2 text-sm text-[#12284B] focus:outline-none focus:border-[#E84B1C]";
const btnPrimary = "inline-flex items-center gap-2 bg-[#E84B1C] hover:bg-[#F0673E] text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-60";
const card = "bg-white rounded-xl border border-[#12284B]/10 p-5 shadow-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#12284B]/70 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ImageUpload({ value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef();
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="h-14 w-14 rounded-lg bg-[#12284B]/5 border border-[#12284B]/10 flex items-center justify-center overflow-hidden shrink-0">
        {value ? <img src={resolveImg(value)} alt="" className="h-full w-full object-cover" /> : <ImageIcon size={20} className="text-[#12284B]/30" />}
      </div>
      <div className="flex items-center gap-2">
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handle} data-testid={testid} />
        <button type="button" onClick={() => ref.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 text-sm border border-[#12284B]/20 rounded-lg px-3 py-1.5 hover:border-[#E84B1C] text-[#12284B]">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Upload
        </button>
        {value && <button type="button" onClick={() => onChange("")} className="text-xs text-red-500">Remove</button>}
      </div>
    </div>
  );
}

/* ---------------- Leads ---------------- */
const CRM_BADGE = {
  synced: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  pending: "bg-amber-100 text-amber-700",
};
function CrmBadge({ lead }) {
  const s = lead.crm_status || "not sent";
  const label = s === "synced" ? `Synced${lead.crm_action ? ` (${lead.crm_action})` : ""}` : s === "disabled" ? "Not sent" : s;
  return (
    <span title={lead.crm_error || ""} className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ${CRM_BADGE[s] || "bg-[#12284B]/5 text-[#12284B]/60"}`}>
      {label}
    </span>
  );
}
function LeadsSection() {
  const [leads, setLeads] = useState(null);
  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this lead?")) return; await api.delete(`/leads/${id}`); load(); };
  const [retrying, setRetrying] = useState(null);
  const retry = async (id) => {
    setRetrying(id);
    try { await api.post(`/admin/leads/${id}/retry-crm`); } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail)); }
    setRetrying(null); load();
  };
  if (!leads) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className={card}>
      <h2 className="font-head font-bold text-lg text-[#12284B] mb-4">Leads ({leads.length})</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead>
            <tr className="text-left text-[#12284B]/60 border-b border-[#12284B]/10">
              <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Hotel</th><th className="py-2 pr-3">Mobile</th>
              <th className="py-2 pr-3">Email</th><th className="py-2 pr-3">City/State</th><th className="py-2 pr-3">Service</th><th className="py-2 pr-3">Cratio CRM</th><th></th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-b border-[#12284B]/5">
                <td className="py-2 pr-3 font-medium text-[#12284B]">{l.full_name}</td>
                <td className="py-2 pr-3">{l.hotel_name}</td>
                <td className="py-2 pr-3">{l.mobile}</td>
                <td className="py-2 pr-3">{l.email}</td>
                <td className="py-2 pr-3">{[l.city, l.state].filter(Boolean).join(", ")}</td>
                <td className="py-2 pr-3">{l.service}</td>
                <td className="py-2 pr-3" data-testid={`lead-crm-status-${l.id}`}>
                  <CrmBadge lead={l} />
                  {l.crm_status !== "synced" && (
                    <button onClick={() => retry(l.id)} disabled={retrying === l.id} className="ml-2 inline-flex items-center gap-1 text-xs text-[#E84B1C] hover:underline" data-testid={`lead-retry-crm-${l.id}`}>
                      <RefreshCw size={12} className={retrying === l.id ? "animate-spin" : ""} /> {l.crm_status === "failed" ? "Retry" : "Send"}
                    </button>
                  )}
                </td>
                <td className="py-2"><button onClick={() => del(l.id)} className="text-red-500" data-testid={`lead-delete-${l.id}`}><Trash2 size={16} /></button></td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan="8" className="py-6 text-center text-[#12284B]/40">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- List sections (testimonials / faqs) ---------------- */
function TestimonialsSection() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/list/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const add = async () => { await api.post("/list/testimonials", { data: { name: "", quote_en: "", quote_hi: "", image: "" } }); load(); };
  const save = async (it) => { await api.put(`/list/testimonials/${it.id}`, { data: it }); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/list/testimonials/${id}`); load(); };
  if (!items) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-head font-bold text-lg text-[#12284B]">Testimonials</h2>
        <button onClick={add} className={btnPrimary} data-testid="add-testimonial"><Plus size={16} /> Add</button>
      </div>
      <p className="text-xs text-[#12284B]/50">If empty, the website shows the built-in default reviews.</p>
      {items.map((it) => <TestimonialRow key={it.id} item={it} onSave={save} onDelete={del} />)}
    </div>
  );
}
function TestimonialRow({ item, onSave, onDelete }) {
  const [f, setF] = useState(item);
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const save = async () => { setSaving(true); await onSave(f); setSaving(false); };
  return (
    <div className={card}>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Name"><input className={inputCls} value={f.name || ""} onChange={(e) => upd("name", e.target.value)} /></Field>
        <Field label="Photo"><ImageUpload value={f.image} onChange={(v) => upd("image", v)} /></Field>
        <Field label="Quote (English)"><textarea rows={3} className={inputCls} value={f.quote_en || ""} onChange={(e) => upd("quote_en", e.target.value)} /></Field>
        <Field label="Quote (Hindi)"><textarea rows={3} className={inputCls} value={f.quote_hi || ""} onChange={(e) => upd("quote_hi", e.target.value)} /></Field>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={save} disabled={saving} className={btnPrimary}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
        <button onClick={() => onDelete(item.id)} className="inline-flex items-center gap-1.5 text-sm text-red-500 border border-red-200 rounded-lg px-3 py-2"><Trash2 size={14} /> Delete</button>
      </div>
    </div>
  );
}

function FaqsSection() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/list/faqs").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const add = async () => { await api.post("/list/faqs", { data: { q_en: "", a_en: "", q_hi: "", a_hi: "" } }); load(); };
  const save = async (it) => { await api.put(`/list/faqs/${it.id}`, { data: it }); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/list/faqs/${id}`); load(); };
  if (!items) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-head font-bold text-lg text-[#12284B]">FAQs</h2>
        <button onClick={add} className={btnPrimary} data-testid="add-faq"><Plus size={16} /> Add</button>
      </div>
      <p className="text-xs text-[#12284B]/50">If empty, the website shows the built-in default FAQs.</p>
      {items.map((it) => <FaqRow key={it.id} item={it} onSave={save} onDelete={del} />)}
    </div>
  );
}
function FaqRow({ item, onSave, onDelete }) {
  const [f, setF] = useState(item);
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const save = async () => { setSaving(true); await onSave(f); setSaving(false); };
  return (
    <div className={card}>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Question (English)"><input className={inputCls} value={f.q_en || ""} onChange={(e) => upd("q_en", e.target.value)} /></Field>
        <Field label="Question (Hindi)"><input className={inputCls} value={f.q_hi || ""} onChange={(e) => upd("q_hi", e.target.value)} /></Field>
        <Field label="Answer (English)"><textarea rows={2} className={inputCls} value={f.a_en || ""} onChange={(e) => upd("a_en", e.target.value)} /></Field>
        <Field label="Answer (Hindi)"><textarea rows={2} className={inputCls} value={f.a_hi || ""} onChange={(e) => upd("a_hi", e.target.value)} /></Field>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={save} disabled={saving} className={btnPrimary}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
        <button onClick={() => onDelete(item.id)} className="inline-flex items-center gap-1.5 text-sm text-red-500 border border-red-200 rounded-lg px-3 py-2"><Trash2 size={14} /> Delete</button>
      </div>
    </div>
  );
}

/* ---------------- Contact ---------------- */
function ContactSection() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content/contact").then((r) => setF(r.data.value || {})).catch(() => setF({}));
  }, []);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const save = async () => { setSaving(true); await api.put("/content/contact", { value: f }); setSaving(false); alert("Saved"); };
  if (!f) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className={card}>
      <h2 className="font-head font-bold text-lg text-[#12284B] mb-4">Contact Information</h2>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Phone 1"><input className={inputCls} value={f.phone || ""} onChange={(e) => upd("phone", e.target.value)} placeholder="+91 ..." data-testid="contact-phone1-input" /></Field>
        <Field label="Phone 2"><input className={inputCls} value={f.phone2 || ""} onChange={(e) => upd("phone2", e.target.value)} placeholder="+91 ..." data-testid="contact-phone2-input" /></Field>
        <Field label="WhatsApp number"><input className={inputCls} value={f.whatsapp || ""} onChange={(e) => upd("whatsapp", e.target.value)} placeholder="+91XXXXXXXXXX" data-testid="contact-whatsapp-input" /></Field>
        <Field label="Email"><input className={inputCls} value={f.email || ""} onChange={(e) => upd("email", e.target.value)} /></Field>
        <Field label="City / Location (English)"><input className={inputCls} value={f.city_en || ""} onChange={(e) => upd("city_en", e.target.value)} /></Field>
        <Field label="City / Location (Hindi)"><input className={inputCls} value={f.city_hi || ""} onChange={(e) => upd("city_hi", e.target.value)} /></Field>
        <Field label="Support Hours (English)"><input className={inputCls} value={f.hours_en || ""} onChange={(e) => upd("hours_en", e.target.value)} /></Field>
        <Field label="Support Hours (Hindi)"><input className={inputCls} value={f.hours_hi || ""} onChange={(e) => upd("hours_hi", e.target.value)} /></Field>
      </div>
      <h3 className="font-head font-semibold text-[#12284B] mt-6 mb-3">Social Links</h3>
      <div className="grid md:grid-cols-2 gap-3">
        {[["social_facebook", "Facebook URL"], ["social_instagram", "Instagram URL"], ["social_linkedin", "LinkedIn URL"], ["social_youtube", "YouTube URL"]].map(([k, label]) => (
          <Field key={k} label={label}><input className={inputCls} value={f[k] || ""} onChange={(e) => upd(k, e.target.value)} placeholder="https://..." data-testid={`contact-${k}-input`} /></Field>
        ))}
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer" data-testid="wa-float-toggle">
        <input type="checkbox" checked={f.wa_float_enabled !== false} onChange={(e) => upd("wa_float_enabled", e.target.checked)} className="accent-[#25D366] h-4 w-4" />
        Show floating WhatsApp button on the website
      </label>
      <button onClick={save} disabled={saving} className={`${btnPrimary} mt-4`} data-testid="save-contact">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
    </div>
  );
}

/* ---------------- Services ---------------- */
function serviceDetailDefaults(slug) {
  const en = translations.en, hi = translations.hi;
  const rich = en.richServiceDetail?.[slug], richHi = hi.richServiceDetail?.[slug];
  if (rich) {
    return {
      heading_en: en.services[slug]?.title || "",
      heading_hi: hi.services[slug]?.title || "",
      body_en: rich.body || "",
      body_hi: richHi?.body || "",
      features_en: (rich.features || []).map((f) => f.title).join("\n"),
      features_hi: (richHi?.features || []).map((f) => f.title).join("\n"),
    };
  }
  const std = en.serviceDetail?.[slug], stdHi = hi.serviceDetail?.[slug];
  return {
    heading_en: en.services[slug]?.title || "",
    heading_hi: hi.services[slug]?.title || "",
    body_en: std?.intro || "",
    body_hi: stdHi?.intro || "",
    features_en: (std?.benefits || []).join("\n"),
    features_hi: (stdHi?.benefits || []).join("\n"),
  };
}
function ServicesSection() {
  const [overrides, setOverrides] = useState(null);
  useEffect(() => {
    api.get("/list/services_meta").then((r) => {
      const map = {};
      r.data.forEach((d) => { map[d.slug] = d; });
      setOverrides(map);
    }).catch(() => setOverrides({}));
  }, []);
  if (!overrides) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  const en = translations.en.services;
  return (
    <div className="space-y-4">
      <h2 className="font-head font-bold text-lg text-[#12284B]">Services</h2>
      <p className="text-xs text-[#12284B]/50">Toggle visibility, override the card title / short description / image, and edit the detail page heading, body & features. Blank fields keep the website defaults.</p>
      {serviceList.map((s) => (
        <ServiceRow key={s.slug} slug={s.slug} defTitle={en[s.slug]?.title} defShort={en[s.slug]?.short} defDetail={serviceDetailDefaults(s.slug)} existing={overrides[s.slug]} />
      ))}
    </div>
  );
}
function ServiceRow({ slug, defTitle, defShort, defDetail, existing }) {
  const [f, setF] = useState(existing || { slug, active: true, title_en: "", title_hi: "", short_en: "", short_hi: "", image: "", heading_en: "", heading_hi: "", body_en: "", body_hi: "", features_en: "", features_hi: "" });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    setSaving(true);
    if (f.id) await api.put(`/list/services_meta/${f.id}`, { data: f });
    else { const { data } = await api.post("/list/services_meta", { data: f }); setF(data); }
    setSaving(false);
  };
  return (
    <div className={card}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-[#12284B]">{defTitle || slug}</div>
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`service-active-${slug}`}>
          <input type="checkbox" checked={f.active !== false} onChange={(e) => upd("active", e.target.checked)} className="accent-[#E84B1C] h-4 w-4" />
          {f.active !== false ? "Active" : "Hidden"}
        </label>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Title (English)"><input className={inputCls} value={f.title_en || ""} onChange={(e) => upd("title_en", e.target.value)} placeholder={defTitle} /></Field>
        <Field label="Title (Hindi)"><input className={inputCls} value={f.title_hi || ""} onChange={(e) => upd("title_hi", e.target.value)} /></Field>
        <Field label="Short (English)"><textarea rows={2} className={inputCls} value={f.short_en || ""} onChange={(e) => upd("short_en", e.target.value)} placeholder={defShort} /></Field>
        <Field label="Short (Hindi)"><textarea rows={2} className={inputCls} value={f.short_hi || ""} onChange={(e) => upd("short_hi", e.target.value)} /></Field>
        <Field label="Card Image"><ImageUpload value={f.image} onChange={(v) => upd("image", v)} /></Field>
      </div>

      <div className="mt-4 border-t border-[#12284B]/10 pt-3">
        <div className="font-head font-semibold text-[#E84B1C] text-sm mb-2">Detail Page</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Detail Heading (English)"><input className={inputCls} value={f.heading_en || ""} onChange={(e) => upd("heading_en", e.target.value)} placeholder={defDetail.heading_en} data-testid={`service-heading-en-${slug}`} /></Field>
          <Field label="Detail Heading (Hindi)"><input className={inputCls} value={f.heading_hi || ""} onChange={(e) => upd("heading_hi", e.target.value)} placeholder={defDetail.heading_hi} /></Field>
          <Field label="Detail Body (English)"><textarea rows={3} className={inputCls} value={f.body_en || ""} onChange={(e) => upd("body_en", e.target.value)} placeholder={defDetail.body_en} /></Field>
          <Field label="Detail Body (Hindi)"><textarea rows={3} className={inputCls} value={f.body_hi || ""} onChange={(e) => upd("body_hi", e.target.value)} placeholder={defDetail.body_hi} /></Field>
          <Field label="Features (English) — one per line"><textarea rows={5} className={inputCls} value={f.features_en || ""} onChange={(e) => upd("features_en", e.target.value)} placeholder={defDetail.features_en} data-testid={`service-features-en-${slug}`} /></Field>
          <Field label="Features (Hindi) — one per line"><textarea rows={5} className={inputCls} value={f.features_hi || ""} onChange={(e) => upd("features_hi", e.target.value)} placeholder={defDetail.features_hi} /></Field>
        </div>
      </div>

      <button onClick={save} disabled={saving} className={`${btnPrimary} mt-3`} data-testid={`service-save-${slug}`}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
    </div>
  );
}

/* ---------------- AI Concierge ---------------- */
function AiSection() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content/ai_concierge").then((r) => setF(r.data.value || defaultAi())).catch(() => setF(defaultAi()));
  }, []);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const updPoint = (i, k, v) => { const pts = [...f.points]; pts[i] = { ...pts[i], [k]: v }; setF({ ...f, points: pts }); };
  const save = async () => { setSaving(true); await api.put("/content/ai_concierge", { value: f }); setSaving(false); alert("Saved"); };
  if (!f) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className={card}>
      <h2 className="font-head font-bold text-lg text-[#12284B] mb-1">WhatsApp AI Concierge</h2>
      <p className="text-xs text-[#12284B]/50 mb-4">Shown on the WhatsApp Automation service page.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Eyebrow (English)"><input className={inputCls} value={f.eyebrow_en || ""} onChange={(e) => upd("eyebrow_en", e.target.value)} /></Field>
        <Field label="Eyebrow (Hindi)"><input className={inputCls} value={f.eyebrow_hi || ""} onChange={(e) => upd("eyebrow_hi", e.target.value)} /></Field>
        <Field label="Heading (English)"><input className={inputCls} value={f.heading_en || ""} onChange={(e) => upd("heading_en", e.target.value)} /></Field>
        <Field label="Heading (Hindi)"><input className={inputCls} value={f.heading_hi || ""} onChange={(e) => upd("heading_hi", e.target.value)} /></Field>
        <Field label="Intro (English)"><textarea rows={2} className={inputCls} value={f.intro_en || ""} onChange={(e) => upd("intro_en", e.target.value)} /></Field>
        <Field label="Intro (Hindi)"><textarea rows={2} className={inputCls} value={f.intro_hi || ""} onChange={(e) => upd("intro_hi", e.target.value)} /></Field>
      </div>
      <div className="mt-4 space-y-3">
        {f.points.map((p, i) => (
          <div key={i} className="grid md:grid-cols-4 gap-2 border-t border-[#12284B]/10 pt-3">
            <input className={inputCls} value={p.title_en || ""} onChange={(e) => updPoint(i, "title_en", e.target.value)} placeholder="Title EN" />
            <input className={inputCls} value={p.title_hi || ""} onChange={(e) => updPoint(i, "title_hi", e.target.value)} placeholder="Title HI" />
            <input className={inputCls} value={p.desc_en || ""} onChange={(e) => updPoint(i, "desc_en", e.target.value)} placeholder="Desc EN" />
            <input className={inputCls} value={p.desc_hi || ""} onChange={(e) => updPoint(i, "desc_hi", e.target.value)} placeholder="Desc HI" />
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-3 mt-4">
        <Field label="Footer (English)"><input className={inputCls} value={f.footer_en || ""} onChange={(e) => upd("footer_en", e.target.value)} /></Field>
        <Field label="Footer (Hindi)"><input className={inputCls} value={f.footer_hi || ""} onChange={(e) => upd("footer_hi", e.target.value)} /></Field>
      </div>
      <button onClick={save} disabled={saving} className={`${btnPrimary} mt-4`} data-testid="save-ai">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
    </div>
  );
}
function defaultAi() {
  const w = translations.en.richServiceDetail?.["whatsapp-automation"]?.aiConcierge;
  const wh = translations.hi.richServiceDetail?.["whatsapp-automation"]?.aiConcierge;
  return {
    eyebrow_en: w?.eyebrow || "", eyebrow_hi: wh?.eyebrow || "",
    heading_en: w?.heading || "", heading_hi: wh?.heading || "",
    intro_en: w?.intro || "", intro_hi: wh?.intro || "",
    points: (w?.points || [{}, {}, {}, {}]).map((p, i) => ({
      title_en: p.title || "", desc_en: p.desc || "",
      title_hi: wh?.points?.[i]?.title || "", desc_hi: wh?.points?.[i]?.desc || "",
    })),
    footer_en: w?.footer || "", footer_hi: wh?.footer || "",
  };
}

/* ---------------- Homepage & About defaults ---------------- */
function heroDefaults() {
  const en = translations.en.hero, hi = translations.hi.hero;
  return {
    heading_en: `${en.line1}\n${en.line2}\n${en.line3}`,
    heading_hi: `${hi.line1}\n${hi.line2}\n${hi.line3}`,
    sub_en: en.sub, sub_hi: hi.sub,
    para_en: en.paragraph, para_hi: hi.paragraph,
    cta_primary_en: en.ctaPrimary, cta_primary_hi: hi.ctaPrimary, cta_primary_link: "",
    cta_secondary_en: en.ctaSecondary, cta_secondary_hi: hi.ctaSecondary, cta_secondary_link: "/services",
    bg_video: HERO_VIDEO_URL,
  };
}
function statsDefaults() {
  return { items: DEFAULT_STATS.map((s) => ({ ...s })) };
}
function whyDefaults() {
  const en = translations.en.whyChoose, hi = translations.hi.whyChoose;
  return {
    title_en: en.title, title_hi: hi.title,
    subtitle_en: en.subtitle, subtitle_hi: hi.subtitle,
    items: en.reasons.map((r, i) => ({
      title_en: r.h, desc_en: r.p,
      title_hi: hi.reasons[i]?.h || "", desc_hi: hi.reasons[i]?.p || "",
      active: true,
    })),
  };
}
function aboutDefaults() {
  const en = translations.en.about, hi = translations.hi.about;
  const heEn = translations.en.hero, heHi = translations.hi.hero;
  return {
    heading_en: `${heEn.line1}\n${heEn.line2}\n${heEn.line3}`,
    heading_hi: `${heHi.line1}\n${heHi.line2}\n${heHi.line3}`,
    subtitle_en: en.title2, subtitle_hi: hi.title2,
    paragraph_en: en.paragraph, paragraph_hi: hi.paragraph,
    story_title_en: en.storyTitle, story_title_hi: hi.storyTitle,
    story_en: en.storyParas.join("\n"), story_hi: hi.storyParas.join("\n"),
    mission_en: en.mission, mission_hi: hi.mission,
    vision_en: en.vision, vision_hi: hi.vision,
    image: "",
  };
}
function sectionsDefaults() {
  const e = translations.en, h = translations.hi;
  return {
    services: {
      eyebrow_en: e.servicesOverview.eyebrow, eyebrow_hi: h.servicesOverview.eyebrow,
      title_en: e.servicesOverview.title, title_hi: h.servicesOverview.title,
      subtitle_en: e.servicesOverview.subtitle, subtitle_hi: h.servicesOverview.subtitle,
      viewall_en: e.servicesOverview.viewAll, viewall_hi: h.servicesOverview.viewAll,
    },
    testimonials: {
      eyebrow_en: e.testimonialsSection.eyebrow, eyebrow_hi: h.testimonialsSection.eyebrow,
      title_en: e.testimonialsSection.title, title_hi: h.testimonialsSection.title,
      subtitle_en: "", subtitle_hi: "",
    },
    faq: {
      eyebrow_en: e.faqSection.eyebrow, eyebrow_hi: h.faqSection.eyebrow,
      title_en: e.faqSection.title, title_hi: h.faqSection.title,
      subtitle_en: "", subtitle_hi: "",
    },
    lead: {
      eyebrow_en: e.lead.eyebrow, eyebrow_hi: h.lead.eyebrow,
      title_en: e.lead.title, title_hi: h.lead.title,
      para_en: e.lead.paragraph, para_hi: h.lead.paragraph,
      bullets_en: e.lead.bullets.join("\n"), bullets_hi: h.lead.bullets.join("\n"),
      whatsapp_en: e.lead.whatsappPrefer, whatsapp_hi: h.lead.whatsappPrefer,
    },
  };
}

/* ---------------- Hero editor ---------------- */
function HeroEditor() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content/homepage_hero").then((r) => setF({ ...heroDefaults(), ...(r.data.value || {}) })).catch(() => setF(heroDefaults()));
  }, []);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const save = async () => { setSaving(true); await api.put("/content/homepage_hero", { value: f }); setSaving(false); alert("Hero saved"); };
  if (!f) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className={card}>
      <h3 className="font-head font-bold text-[#12284B] mb-1">Hero Section</h3>
      <p className="text-xs text-[#12284B]/50 mb-4">Blank fields fall back to the built-in default. In the heading, each line becomes a new line; the first line is highlighted orange.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Heading (English)"><textarea rows={3} className={inputCls} value={f.heading_en || ""} onChange={(e) => upd("heading_en", e.target.value)} data-testid="hero-heading-en" /></Field>
        <Field label="Heading (Hindi)"><textarea rows={3} className={inputCls} value={f.heading_hi || ""} onChange={(e) => upd("heading_hi", e.target.value)} /></Field>
        <Field label="Subheading (English)"><textarea rows={2} className={inputCls} value={f.sub_en || ""} onChange={(e) => upd("sub_en", e.target.value)} /></Field>
        <Field label="Subheading (Hindi)"><textarea rows={2} className={inputCls} value={f.sub_hi || ""} onChange={(e) => upd("sub_hi", e.target.value)} /></Field>
        <Field label="Description (English)"><textarea rows={3} className={inputCls} value={f.para_en || ""} onChange={(e) => upd("para_en", e.target.value)} /></Field>
        <Field label="Description (Hindi)"><textarea rows={3} className={inputCls} value={f.para_hi || ""} onChange={(e) => upd("para_hi", e.target.value)} /></Field>
        <Field label="Primary Button Text (English)"><input className={inputCls} value={f.cta_primary_en || ""} onChange={(e) => upd("cta_primary_en", e.target.value)} /></Field>
        <Field label="Primary Button Text (Hindi)"><input className={inputCls} value={f.cta_primary_hi || ""} onChange={(e) => upd("cta_primary_hi", e.target.value)} /></Field>
        <Field label="Primary Button Link"><input className={inputCls} value={f.cta_primary_link || ""} onChange={(e) => upd("cta_primary_link", e.target.value)} placeholder="Blank = WhatsApp link. e.g. /contact or https://..." /></Field>
        <div />
        <Field label="Secondary Button Text (English)"><input className={inputCls} value={f.cta_secondary_en || ""} onChange={(e) => upd("cta_secondary_en", e.target.value)} /></Field>
        <Field label="Secondary Button Text (Hindi)"><input className={inputCls} value={f.cta_secondary_hi || ""} onChange={(e) => upd("cta_secondary_hi", e.target.value)} /></Field>
        <Field label="Secondary Button Link"><input className={inputCls} value={f.cta_secondary_link || ""} onChange={(e) => upd("cta_secondary_link", e.target.value)} placeholder="e.g. /services" /></Field>
        <Field label="Hero Background Video URL"><input className={inputCls} value={f.bg_video || ""} onChange={(e) => upd("bg_video", e.target.value)} placeholder="https://...mp4 (blank = default)" data-testid="hero-bg-video-url" /></Field>
      </div>
      <button onClick={save} disabled={saving} className={`${btnPrimary} mt-4`} data-testid="save-hero">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Hero</button>
    </div>
  );
}

/* ---------------- Stats editor ---------------- */
function StatsEditor() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content/homepage_stats").then((r) => setItems(r.data.value?.items || statsDefaults().items)).catch(() => setItems(statsDefaults().items));
  }, []);
  const upd = (i, k, v) => { const n = [...items]; n[i] = { ...n[i], [k]: v }; setItems(n); };
  const save = async () => { setSaving(true); await api.put("/content/homepage_stats", { value: { items } }); setSaving(false); alert("Stats saved"); };
  if (!items) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className={card}>
      <h3 className="font-head font-bold text-[#12284B] mb-1">Statistics / Counter Cards</h3>
      <p className="text-xs text-[#12284B]/50 mb-4">Edit the number & label of each hero card, or disable a card to hide it. Cards 4 & 5 display the label only (number is not shown by design).</p>
      <div className="space-y-3">
        {items.map((s, i) => (
          <div key={i} className="grid md:grid-cols-[70px_110px_1fr_1fr_110px] gap-2 items-end border-t border-[#12284B]/10 pt-3" data-testid={`stat-row-${i}`}>
            <div className="text-xs font-semibold text-[#12284B]/70">Card {i + 1}</div>
            <Field label="Number"><input className={inputCls} value={s.value || ""} onChange={(e) => upd(i, "value", e.target.value)} placeholder={i > 2 ? "n/a" : "e.g. 1500+"} /></Field>
            <Field label="Label (English)"><input className={inputCls} value={s.label_en || ""} onChange={(e) => upd(i, "label_en", e.target.value)} /></Field>
            <Field label="Label (Hindi)"><input className={inputCls} value={s.label_hi || ""} onChange={(e) => upd(i, "label_hi", e.target.value)} /></Field>
            <label className="flex items-center gap-2 text-sm cursor-pointer pb-2" data-testid={`stat-active-${i}`}>
              <input type="checkbox" checked={s.active !== false} onChange={(e) => upd(i, "active", e.target.checked)} className="accent-[#E84B1C] h-4 w-4" />
              {s.active !== false ? "Shown" : "Hidden"}
            </label>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} className={`${btnPrimary} mt-4`} data-testid="save-stats">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Stats</button>
    </div>
  );
}

/* ---------------- Why Choose Us editor ---------------- */
function WhyEditor() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content/homepage_why").then((r) => {
      const v = r.data.value;
      setF(v && v.items ? { ...whyDefaults(), ...v } : whyDefaults());
    }).catch(() => setF(whyDefaults()));
  }, []);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const updItem = (i, k, v) => { const n = [...f.items]; n[i] = { ...n[i], [k]: v }; setF({ ...f, items: n }); };
  const addItem = () => setF({ ...f, items: [...f.items, { title_en: "", title_hi: "", desc_en: "", desc_hi: "", active: true }] });
  const delItem = (i) => setF({ ...f, items: f.items.filter((_, idx) => idx !== i) });
  const save = async () => { setSaving(true); await api.put("/content/homepage_why", { value: f }); setSaving(false); alert("Why Choose Us saved"); };
  if (!f) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className={card}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-head font-bold text-[#12284B]">Why Choose Us</h3>
        <button onClick={addItem} className={btnPrimary} data-testid="add-why-item"><Plus size={16} /> Add item</button>
      </div>
      <p className="text-xs text-[#12284B]/50 mb-4">Icons are assigned automatically. Disable an item to hide it from the website.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Section Heading (English)"><input className={inputCls} value={f.title_en || ""} onChange={(e) => upd("title_en", e.target.value)} data-testid="why-title-en" /></Field>
        <Field label="Section Heading (Hindi)"><input className={inputCls} value={f.title_hi || ""} onChange={(e) => upd("title_hi", e.target.value)} /></Field>
        <Field label="Subtitle (English)"><textarea rows={2} className={inputCls} value={f.subtitle_en || ""} onChange={(e) => upd("subtitle_en", e.target.value)} /></Field>
        <Field label="Subtitle (Hindi)"><textarea rows={2} className={inputCls} value={f.subtitle_hi || ""} onChange={(e) => upd("subtitle_hi", e.target.value)} /></Field>
      </div>
      <div className="mt-4 space-y-3">
        {f.items.map((it, i) => (
          <div key={i} className="border-t border-[#12284B]/10 pt-3" data-testid={`why-item-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-[#12284B]/70">Item {i + 1}</span>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`why-active-${i}`}>
                  <input type="checkbox" checked={it.active !== false} onChange={(e) => updItem(i, "active", e.target.checked)} className="accent-[#E84B1C] h-4 w-4" />
                  {it.active !== false ? "Shown" : "Hidden"}
                </label>
                <button onClick={() => delItem(i)} className="text-red-500" data-testid={`why-delete-${i}`}><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              <input className={inputCls} value={it.title_en || ""} onChange={(e) => updItem(i, "title_en", e.target.value)} placeholder="Title EN" />
              <input className={inputCls} value={it.title_hi || ""} onChange={(e) => updItem(i, "title_hi", e.target.value)} placeholder="Title HI" />
              <textarea rows={2} className={inputCls} value={it.desc_en || ""} onChange={(e) => updItem(i, "desc_en", e.target.value)} placeholder="Description EN" />
              <textarea rows={2} className={inputCls} value={it.desc_hi || ""} onChange={(e) => updItem(i, "desc_hi", e.target.value)} placeholder="Description HI" />
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} className={`${btnPrimary} mt-4`} data-testid="save-why">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Why Choose Us</button>
    </div>
  );
}

function HomepageSection() {
  return (
    <div className="space-y-6">
      <h2 className="font-head font-bold text-lg text-[#12284B]">Homepage Content</h2>
      <HeroEditor />
      <StatsEditor />
      <WhyEditor />
      <SectionsEditor />
      <MapEditor />
    </div>
  );
}

/* ---------------- India presence map editor ---------------- */
function MapEditor() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content/homepage_map").then((r) => {
      setF({ ...indiaMapDefault, ...(r.data.value || {}), states: (r.data.value?.states?.length ? r.data.value.states : indiaMapDefault.states) });
    }).catch(() => setF({ ...indiaMapDefault }));
  }, []);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const toggleState = (name) => {
    const has = f.states.includes(name);
    setF({ ...f, states: has ? f.states.filter((s) => s !== name) : [...f.states, name] });
  };
  const save = async () => { setSaving(true); await api.put("/content/homepage_map", { value: f }); setSaving(false); alert("India map section saved"); };
  if (!f) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className={card}>
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="font-head font-bold text-[#12284B]">India Presence Map</h3>
        <label className="flex items-center gap-2 text-sm text-[#12284B] cursor-pointer">
          {f.enabled !== false ? "Visible on homepage" : "Hidden"}
          <Switch checked={f.enabled !== false} onCheckedChange={(v) => upd("enabled", v)} data-testid="map-visibility-toggle" />
        </label>
      </div>
      <p className="text-xs text-[#12284B]/50 mb-4">Edit the heading, subtitle, the three stat numbers, and tick the states that should show a pin on the map. Blank text fields fall back to defaults.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Eyebrow (English)"><input className={inputCls} value={f.eyebrow_en || ""} onChange={(e) => upd("eyebrow_en", e.target.value)} /></Field>
        <Field label="Eyebrow (Hindi)"><input className={inputCls} value={f.eyebrow_hi || ""} onChange={(e) => upd("eyebrow_hi", e.target.value)} /></Field>
        <Field label="Heading (English)"><input className={inputCls} value={f.heading_en || ""} onChange={(e) => upd("heading_en", e.target.value)} data-testid="map-heading-en" /></Field>
        <Field label="Heading (Hindi)"><input className={inputCls} value={f.heading_hi || ""} onChange={(e) => upd("heading_hi", e.target.value)} /></Field>
        <Field label="Subtitle (English)"><textarea rows={2} className={inputCls} value={f.subtitle_en || ""} onChange={(e) => upd("subtitle_en", e.target.value)} /></Field>
        <Field label="Subtitle (Hindi)"><textarea rows={2} className={inputCls} value={f.subtitle_hi || ""} onChange={(e) => upd("subtitle_hi", e.target.value)} /></Field>
      </div>
      <div className="grid md:grid-cols-3 gap-3 mt-3">
        <Field label="States Count"><input className={inputCls} value={f.states_count || ""} onChange={(e) => upd("states_count", e.target.value)} data-testid="map-states-count" placeholder="24+" /></Field>
        <Field label="Cities Count"><input className={inputCls} value={f.cities_count || ""} onChange={(e) => upd("cities_count", e.target.value)} placeholder="318" /></Field>
        <Field label="Accommodations Count"><input className={inputCls} value={f.accommodations_count || ""} onChange={(e) => upd("accommodations_count", e.target.value)} placeholder="1500+" /></Field>
      </div>
      <div className="mt-3">
        <Field label="Map Image URL"><input className={inputCls} value={f.map_image || ""} onChange={(e) => upd("map_image", e.target.value)} placeholder="https://...png (blank = default)" data-testid="map-image-url" /></Field>
      </div>
      <div className="mt-4">
        <div className="text-sm font-semibold text-[#12284B] mb-2">Active states / pins ({f.states.length} selected)</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-1.5 gap-x-3 max-h-72 overflow-y-auto pr-1 border border-[#12284B]/10 rounded-xl p-3">
          {INDIA_STATES.map((name) => (
            <label key={name} className="flex items-center gap-2 text-sm cursor-pointer text-[#12284B]/80" data-testid={`map-state-${name.replace(/\s+/g, "-").toLowerCase()}`}>
              <input type="checkbox" checked={f.states.includes(name)} onChange={() => toggleState(name)} className="accent-[#E84B1C] h-4 w-4 shrink-0" />
              <span className="truncate">{name}</span>
            </label>
          ))}
        </div>
      </div>
      <button onClick={save} disabled={saving} className={`${btnPrimary} mt-4`} data-testid="save-map">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save India Map</button>
    </div>
  );
}

/* ---------------- Section headings editor ---------------- */
function SectionsEditor() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content/homepage_sections").then((r) => {
      const v = r.data.value || {};
      const d = sectionsDefaults();
      setF({
        services: { ...d.services, ...(v.services || {}) },
        testimonials: { ...d.testimonials, ...(v.testimonials || {}) },
        faq: { ...d.faq, ...(v.faq || {}) },
        lead: { ...d.lead, ...(v.lead || {}) },
      });
    }).catch(() => setF(sectionsDefaults()));
  }, []);
  const upd = (grp, k, v) => setF({ ...f, [grp]: { ...f[grp], [k]: v } });
  const save = async () => { setSaving(true); await api.put("/content/homepage_sections", { value: f }); setSaving(false); alert("Section headings saved"); };
  if (!f) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  const pair = (grp, k, label, area) => (
    <Field label={label}>
      {area
        ? <textarea rows={2} className={inputCls} value={f[grp][k] || ""} onChange={(e) => upd(grp, k, e.target.value)} data-testid={`sec-${grp}-${k}`} />
        : <input className={inputCls} value={f[grp][k] || ""} onChange={(e) => upd(grp, k, e.target.value)} data-testid={`sec-${grp}-${k}`} />}
    </Field>
  );
  return (
    <div className={card}>
      <h3 className="font-head font-bold text-[#12284B] mb-1">Section Headings & Labels</h3>
      <p className="text-xs text-[#12284B]/50 mb-4">Edit the eyebrow labels, headings and subtitles for the homepage sections below. Blank fields fall back to the built-in default (subtitles left blank stay hidden).</p>

      <div className="mb-5">
        <div className="font-head font-semibold text-[#E84B1C] text-sm mb-2">Services Overview</div>
        <div className="grid md:grid-cols-2 gap-3">
          {pair("services", "eyebrow_en", "Eyebrow (English)")}
          {pair("services", "eyebrow_hi", "Eyebrow (Hindi)")}
          {pair("services", "title_en", "Heading (English)")}
          {pair("services", "title_hi", "Heading (Hindi)")}
          {pair("services", "subtitle_en", "Subtitle (English)", true)}
          {pair("services", "subtitle_hi", "Subtitle (Hindi)", true)}
          {pair("services", "viewall_en", "\"View All\" Button (English)")}
          {pair("services", "viewall_hi", "\"View All\" Button (Hindi)")}
        </div>
      </div>

      <div className="mb-5 border-t border-[#12284B]/10 pt-4">
        <div className="font-head font-semibold text-[#E84B1C] text-sm mb-2">Testimonials</div>
        <div className="grid md:grid-cols-2 gap-3">
          {pair("testimonials", "eyebrow_en", "Eyebrow (English)")}
          {pair("testimonials", "eyebrow_hi", "Eyebrow (Hindi)")}
          {pair("testimonials", "title_en", "Heading (English)")}
          {pair("testimonials", "title_hi", "Heading (Hindi)")}
          {pair("testimonials", "subtitle_en", "Subtitle (English)", true)}
          {pair("testimonials", "subtitle_hi", "Subtitle (Hindi)", true)}
        </div>
      </div>

      <div className="mb-5 border-t border-[#12284B]/10 pt-4">
        <div className="font-head font-semibold text-[#E84B1C] text-sm mb-2">FAQ</div>
        <div className="grid md:grid-cols-2 gap-3">
          {pair("faq", "eyebrow_en", "Eyebrow (English)")}
          {pair("faq", "eyebrow_hi", "Eyebrow (Hindi)")}
          {pair("faq", "title_en", "Heading (English)")}
          {pair("faq", "title_hi", "Heading (Hindi)")}
          {pair("faq", "subtitle_en", "Subtitle (English)", true)}
          {pair("faq", "subtitle_hi", "Subtitle (Hindi)", true)}
        </div>
      </div>

      <div className="border-t border-[#12284B]/10 pt-4">
        <div className="font-head font-semibold text-[#E84B1C] text-sm mb-2">Lead Form</div>
        <div className="grid md:grid-cols-2 gap-3">
          {pair("lead", "eyebrow_en", "Eyebrow (English)")}
          {pair("lead", "eyebrow_hi", "Eyebrow (Hindi)")}
          {pair("lead", "title_en", "Heading (English)")}
          {pair("lead", "title_hi", "Heading (Hindi)")}
          {pair("lead", "para_en", "Description (English)", true)}
          {pair("lead", "para_hi", "Description (Hindi)", true)}
          <Field label="Bullet Points (English) — one per line"><textarea rows={4} className={inputCls} value={f.lead.bullets_en || ""} onChange={(e) => upd("lead", "bullets_en", e.target.value)} data-testid="sec-lead-bullets_en" /></Field>
          <Field label="Bullet Points (Hindi) — one per line"><textarea rows={4} className={inputCls} value={f.lead.bullets_hi || ""} onChange={(e) => upd("lead", "bullets_hi", e.target.value)} /></Field>
          {pair("lead", "whatsapp_en", "\"Prefer WhatsApp\" Line (English)")}
          {pair("lead", "whatsapp_hi", "\"Prefer WhatsApp\" Line (Hindi)")}
        </div>
      </div>

      <button onClick={save} disabled={saving} className={`${btnPrimary} mt-4`} data-testid="save-sections">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Section Headings</button>
    </div>
  );
}

/* ---------------- About Page editor ---------------- */
function AboutPageSection() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content/about_page").then((r) => setF({ ...aboutDefaults(), ...(r.data.value || {}) })).catch(() => setF(aboutDefaults()));
  }, []);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const save = async () => { setSaving(true); await api.put("/content/about_page", { value: f }); setSaving(false); alert("About page saved"); };
  if (!f) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className={card}>
      <h2 className="font-head font-bold text-lg text-[#12284B] mb-1">About Page</h2>
      <p className="text-xs text-[#12284B]/50 mb-4">Blank fields fall back to the built-in default. Each line of the heading and story becomes a new line/paragraph.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Heading (English)"><textarea rows={3} className={inputCls} value={f.heading_en || ""} onChange={(e) => upd("heading_en", e.target.value)} data-testid="about-heading-en" /></Field>
        <Field label="Heading (Hindi)"><textarea rows={3} className={inputCls} value={f.heading_hi || ""} onChange={(e) => upd("heading_hi", e.target.value)} /></Field>
        <Field label="Subtitle (English)"><textarea rows={2} className={inputCls} value={f.subtitle_en || ""} onChange={(e) => upd("subtitle_en", e.target.value)} /></Field>
        <Field label="Subtitle (Hindi)"><textarea rows={2} className={inputCls} value={f.subtitle_hi || ""} onChange={(e) => upd("subtitle_hi", e.target.value)} /></Field>
        <Field label="Intro Paragraph (English)"><textarea rows={3} className={inputCls} value={f.paragraph_en || ""} onChange={(e) => upd("paragraph_en", e.target.value)} /></Field>
        <Field label="Intro Paragraph (Hindi)"><textarea rows={3} className={inputCls} value={f.paragraph_hi || ""} onChange={(e) => upd("paragraph_hi", e.target.value)} /></Field>
        <Field label="Story Title (English)"><input className={inputCls} value={f.story_title_en || ""} onChange={(e) => upd("story_title_en", e.target.value)} /></Field>
        <Field label="Story Title (Hindi)"><input className={inputCls} value={f.story_title_hi || ""} onChange={(e) => upd("story_title_hi", e.target.value)} /></Field>
        <Field label="Story (English) — one paragraph per line"><textarea rows={5} className={inputCls} value={f.story_en || ""} onChange={(e) => upd("story_en", e.target.value)} /></Field>
        <Field label="Story (Hindi) — one paragraph per line"><textarea rows={5} className={inputCls} value={f.story_hi || ""} onChange={(e) => upd("story_hi", e.target.value)} /></Field>
        <Field label="Mission (English)"><textarea rows={3} className={inputCls} value={f.mission_en || ""} onChange={(e) => upd("mission_en", e.target.value)} /></Field>
        <Field label="Mission (Hindi)"><textarea rows={3} className={inputCls} value={f.mission_hi || ""} onChange={(e) => upd("mission_hi", e.target.value)} /></Field>
        <Field label="Vision (English)"><textarea rows={3} className={inputCls} value={f.vision_en || ""} onChange={(e) => upd("vision_en", e.target.value)} /></Field>
        <Field label="Vision (Hindi)"><textarea rows={3} className={inputCls} value={f.vision_hi || ""} onChange={(e) => upd("vision_hi", e.target.value)} /></Field>
        <Field label="Story Image"><ImageUpload value={f.image} onChange={(v) => upd("image", v)} testid="about-image-upload" /></Field>
      </div>
      <button onClick={save} disabled={saving} className={`${btnPrimary} mt-4`} data-testid="save-about">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save About Page</button>
    </div>
  );
}

/* ---------------- Privacy Policy editor ---------------- */
function PrivacyPolicySection() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content/privacy_policy").then((r) => {
      const v = r.data.value || {};
      setF({
        title: v.title || privacyPolicyDefault.title,
        effective_date: v.effective_date || privacyPolicyDefault.effective_date,
        last_updated: v.last_updated || privacyPolicyDefault.last_updated,
        intro: v.intro || privacyPolicyDefault.intro,
        sections: (Array.isArray(v.sections) && v.sections.length ? v.sections : privacyPolicyDefault.sections).map((s) => ({ ...s })),
      });
    }).catch(() => setF({ ...privacyPolicyDefault, sections: privacyPolicyDefault.sections.map((s) => ({ ...s })) }));
  }, []);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const updSec = (i, k, v) => { const n = [...f.sections]; n[i] = { ...n[i], [k]: v }; setF({ ...f, sections: n }); };
  const save = async () => { setSaving(true); await api.put("/content/privacy_policy", { value: f }); setSaving(false); alert("Privacy Policy saved"); };
  if (!f) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className={card}>
      <h2 className="font-head font-bold text-lg text-[#12284B] mb-1">Privacy Policy</h2>
      <p className="text-xs text-[#12284B]/50 mb-4">Edit the page title, dates, intro, and each section's heading & body. Use line breaks inside a body for separate paragraphs. Blank fields fall back to the built-in default.</p>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Page Title"><input className={inputCls} value={f.title || ""} onChange={(e) => upd("title", e.target.value)} data-testid="privacy-title" /></Field>
        <Field label="Effective Date"><input className={inputCls} value={f.effective_date || ""} onChange={(e) => upd("effective_date", e.target.value)} /></Field>
        <Field label="Last Updated"><input className={inputCls} value={f.last_updated || ""} onChange={(e) => upd("last_updated", e.target.value)} /></Field>
      </div>
      <div className="mt-3">
        <Field label="Intro"><textarea rows={3} className={inputCls} value={f.intro || ""} onChange={(e) => upd("intro", e.target.value)} /></Field>
      </div>
      <div className="mt-4 space-y-4">
        {f.sections.map((s, i) => (
          <div key={i} className="border-t border-[#12284B]/10 pt-3" data-testid={`privacy-section-${i}`}>
            <div className="text-xs font-semibold text-[#12284B]/70 mb-2">Section {i + 1}</div>
            <Field label="Heading"><input className={inputCls} value={s.heading || ""} onChange={(e) => updSec(i, "heading", e.target.value)} data-testid={`privacy-heading-${i}`} /></Field>
            <div className="mt-2"><Field label="Body"><textarea rows={4} className={inputCls} value={s.body || ""} onChange={(e) => updSec(i, "body", e.target.value)} data-testid={`privacy-body-${i}`} /></Field></div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} className={`${btnPrimary} mt-4`} data-testid="save-privacy">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Privacy Policy</button>
    </div>
  );
}

/* ---------------- Terms & Conditions editor ---------------- */
function TermsConditionsSection() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content/terms_conditions").then((r) => {
      const v = r.data.value || {};
      setF({
        title: v.title || termsConditionsDefault.title,
        effective_date: v.effective_date || termsConditionsDefault.effective_date,
        intro: v.intro || termsConditionsDefault.intro,
        sections: (Array.isArray(v.sections) && v.sections.length ? v.sections : termsConditionsDefault.sections).map((s) => ({ ...s })),
      });
    }).catch(() => setF({ ...termsConditionsDefault, sections: termsConditionsDefault.sections.map((s) => ({ ...s })) }));
  }, []);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const updSec = (i, k, v) => { const n = [...f.sections]; n[i] = { ...n[i], [k]: v }; setF({ ...f, sections: n }); };
  const save = async () => { setSaving(true); await api.put("/content/terms_conditions", { value: f }); setSaving(false); alert("Terms & Conditions saved"); };
  if (!f) return <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className={card}>
      <h2 className="font-head font-bold text-lg text-[#12284B] mb-1">Terms & Conditions</h2>
      <p className="text-xs text-[#12284B]/50 mb-4">Edit the page title, effective date, intro, and each section's heading & body. Use line breaks inside a body for separate paragraphs. Blank fields fall back to the built-in default.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Page Title"><input className={inputCls} value={f.title || ""} onChange={(e) => upd("title", e.target.value)} data-testid="terms-title" /></Field>
        <Field label="Effective Date"><input className={inputCls} value={f.effective_date || ""} onChange={(e) => upd("effective_date", e.target.value)} /></Field>
      </div>
      <div className="mt-3">
        <Field label="Intro"><textarea rows={3} className={inputCls} value={f.intro || ""} onChange={(e) => upd("intro", e.target.value)} /></Field>
      </div>
      <div className="mt-4 space-y-4">
        {f.sections.map((s, i) => (
          <div key={i} className="border-t border-[#12284B]/10 pt-3" data-testid={`terms-section-${i}`}>
            <div className="text-xs font-semibold text-[#12284B]/70 mb-2">Section {i + 1}</div>
            <Field label="Heading"><input className={inputCls} value={s.heading || ""} onChange={(e) => updSec(i, "heading", e.target.value)} data-testid={`terms-heading-${i}`} /></Field>
            <div className="mt-2"><Field label="Body"><textarea rows={4} className={inputCls} value={s.body || ""} onChange={(e) => updSec(i, "body", e.target.value)} data-testid={`terms-body-${i}`} /></Field></div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} className={`${btnPrimary} mt-4`} data-testid="save-terms">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Terms & Conditions</button>
    </div>
  );
}


export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const doLogout = async () => { await logout(); navigate("/admin", { replace: true }); };
  return (
    <div className="min-h-screen bg-[#F4F6FA]">
      <header className="bg-[#12284B] text-white">
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="font-head font-bold">Superhost Cloud — Admin</div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-white/70 hidden sm:inline">{admin?.email}</span>
            <button onClick={doLogout} className="inline-flex items-center gap-1.5 bg-[#E84B1C] hover:bg-[#F0673E] px-3 py-1.5 rounded-lg" data-testid="admin-logout"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-5 py-6 grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? "bg-[#12284B] text-white" : "text-[#12284B] hover:bg-[#12284B]/5"}`}>
                <Icon size={17} /> {t.label}
              </button>
            );
          })}
        </aside>
        <main>
          {tab === "leads" && <LeadsSection />}
          {tab === "homepage" && <HomepageSection />}
          {tab === "about" && <AboutPageSection />}
          {tab === "testimonials" && <TestimonialsSection />}
          {tab === "faqs" && <FaqsSection />}
          {tab === "contact" && <ContactSection />}
          {tab === "services" && <ServicesSection />}
          {tab === "ai" && <AiSection />}
          {tab === "privacy" && <PrivacyPolicySection />}
          {tab === "terms" && <TermsConditionsSection />}
          {tab === "integrations" && <CratioSection />}
        </main>
      </div>
    </div>
  );
}
